import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import DealInformationFormStep1 from './pages/deal-information-form-step-1';
import DocumentUploadStep2 from './pages/document-upload-step-2';
import DealConfirmationOverview from './pages/deal-confirmation-overview';
import BrandGuidelinesSetup from './pages/brand-guidelines-setup';
import BusinessInformationForm from './pages/business-information-form';
import InvestorRelationsContact from './pages/investor-relations-contact';
import CalendarBooking from './pages/calendar-booking';
import OnboardingComplete from './pages/onboarding-complete';
import { getOnboardingStatus, getLocationId, getUserEmail } from './utils/onboardingApi';

/**
 * Mapping from API step names (used in saveProgress calls) to route paths.
 * The API stores the current_step value when saveProgress is called,
 * and getOnboardingStatus() returns it as currentStep.
 */
const STEP_TO_ROUTE = {
  'deal-info-1': '/deal-information-form-step-1',
  'documents': '/document-upload-step-2',
  'deal-confirmation': '/deal-confirmation-overview',
  'brand-guidelines': '/brand-guidelines-setup',
  'business-info': '/business-information-form',
  'ir-contact': '/investor-relations-contact',
  'calendar-booking': '/calendar-booking',
  'complete': '/onboarding-complete',
};

/**
 * Component that checks the user's onboarding status on mount and redirects
 * to the last saved step. If no saved step exists or the API call fails,
 * it falls through to render the first form step (DealInformationFormStep1).
 */
const StepRedirect = () => {
  const [isChecking, setIsChecking] = useState(true);
  const [redirectTo, setRedirectTo] = useState(null);

  useEffect(() => {
    const locationId = getLocationId();
    const email = getUserEmail();

    // If no identifiers are available, skip the check and show the first step
    if (!locationId && !email) {
      setIsChecking(false);
      return;
    }

    getOnboardingStatus()
      .then((status) => {
        if (status?.completed) {
          setRedirectTo('/onboarding-complete');
          return;
        }

        const currentStep = status?.currentStep;
        if (currentStep && STEP_TO_ROUTE[currentStep]) {
          // Only redirect if the saved step is beyond the first step
          if (currentStep !== 'deal-info-1') {
            setRedirectTo(STEP_TO_ROUTE[currentStep]);
          }
        }
      })
      .catch((err) => {
        console.error('Failed to check onboarding status for redirect:', err);
        // On error, stay on the first step
      })
      .finally(() => {
        setIsChecking(false);
      });
  }, []);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return <DealInformationFormStep1 />;
};

const Routes = () => {
  return (
    <BrowserRouter basename="/onboard/v2">
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        {/* Onboarding Flow */}
        <Route path="/" element={<StepRedirect />} />
        <Route path="/deal-information-form-step-1" element={<DealInformationFormStep1 />} />
        <Route path="/document-upload-step-2" element={<DocumentUploadStep2 />} />
        <Route path="/deal-confirmation-overview" element={<DealConfirmationOverview />} />
        <Route path="/brand-guidelines-setup" element={<BrandGuidelinesSetup />} />
        <Route path="/business-information-form" element={<BusinessInformationForm />} />
        <Route path="/investor-relations-contact" element={<InvestorRelationsContact />} />
        <Route path="/calendar-booking" element={<CalendarBooking />} />
        <Route path="/onboarding-complete" element={<OnboardingComplete />} />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
